import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button, FoodsIcon } from "@foodpilot/foods";
import {
    ConsentGovernanceItem,
    ConsentGovernanceVersion,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import {
    getConsentGovernanceErrorMessage,
    useUpsertAdminConsentItemVersion,
} from "src/api/client-api/consent/useManageConsentGovernanceAdmin";
import { type Locale } from "src/api/locales/useLocales";
import { useSnackbar } from "src/utils/useSnackbar";
import { ConsentVersionGlobalFields, ConsentVersionLocalizedFields } from "./components/ConsentVersionModalFields";
import { isConsentVersionArchived } from "./consentVersionStatus";
import { useConsentVersionLocalizedQueries } from "./hooks/useConsentVersionLocalizedQueries";
import {
    buildConsentVersionPayload,
    type ConsentItemOption,
    getConsentItemIri,
    ConsentItemVersionFormSchema,
    type ConsentItemVersionFormData,
    type ConsentVersionLocalizedDraft,
    getConsentVersionGlobalSyncKey,
    getFallbackConsentItemOption,
    mapConsentItemToOption,
} from "./utils/consentVersionModalModel";

type ConsentItemVersionModalProps = {
    consentItems: ConsentGovernanceItem[];
    consentItemType?: "document" | "opt_in";
    consentVersion?: ConsentGovernanceVersion;
    locale: string;
    localeOptions?: Array<Locale & { code: string }>;
    onClose: () => void;
    onSuccess?: (version: ConsentGovernanceVersion) => void;
    open: boolean;
    preselectedConsentItem?: ConsentGovernanceItem;
};

export const ConsentItemVersionModal = (props: ConsentItemVersionModalProps) => {
    const {
        consentItems,
        consentItemType,
        consentVersion,
        locale,
        localeOptions = [],
        onClose,
        onSuccess,
        open,
        preselectedConsentItem,
    } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const mutation = useUpsertAdminConsentItemVersion();
    const [editingLocale, setEditingLocale] = useState(locale);
    const [globalContentSyncKey, setGlobalContentSyncKey] = useState<string | null>(null);
    const [localizedDrafts, setLocalizedDrafts] = useState<Record<string, ConsentVersionLocalizedDraft>>({});
    const isArchivedVersion = isConsentVersionArchived(consentVersion);
    const isDefaultEditingLocale = useMemo(() => {
        const defaultLocaleOption = localeOptions.find((option) => option.isDefault);

        return defaultLocaleOption ? defaultLocaleOption.code === editingLocale : editingLocale === locale;
    }, [editingLocale, locale, localeOptions]);
    const {
        hasAnyLocalizedConsentVersionField,
        hasLocalizedContent,
        hasLocalizedDocumentUrl,
        hasLocalizedSummary,
        hasLocalizedTitle,
        isLocalizedContentFetching,
        localizedConsentVersion,
    } = useConsentVersionLocalizedQueries({
        consentVersion,
        editingLocale,
        isDefaultEditingLocale,
        open,
    });
    const localizedTitle = consentVersion && hasLocalizedTitle ? (localizedConsentVersion?.title ?? "") : "";
    const localizedSummary = consentVersion && hasLocalizedSummary ? (localizedConsentVersion?.summary ?? "") : "";
    const localizedContent = consentVersion && hasLocalizedContent ? (localizedConsentVersion?.content ?? "") : "";
    const localizedDocumentUrl =
        consentVersion && hasLocalizedDocumentUrl ? (localizedConsentVersion?.documentUrl ?? "") : "";

    const {
        control,
        getValues,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isValid },
    } = useForm<ConsentItemVersionFormData>({
        mode: "onChange",
        resolver: zodResolver(ConsentItemVersionFormSchema),
        defaultValues: {
            consentItemIri: "",
            version: "",
            title: "",
            summary: "",
            content: "",
            documentUrl: "",
            published: false,
        },
    });

    const activeLocalizedDraft = localizedDrafts[editingLocale];
    const isActiveLocalizedDraftSynchronized =
        activeLocalizedDraft?.initialized === true &&
        activeLocalizedDraft.title === localizedTitle &&
        activeLocalizedDraft.summary === localizedSummary &&
        activeLocalizedDraft.content === localizedContent &&
        activeLocalizedDraft.documentUrl === localizedDocumentUrl;
    const isLocalizedContentLoading =
        consentVersion !== undefined &&
        !activeLocalizedDraft?.dirty &&
        (isLocalizedContentFetching || !isActiveLocalizedDraftSynchronized);
    const formTitle =
        activeLocalizedDraft?.initialized ? activeLocalizedDraft.title
        : isLocalizedContentFetching ? ""
        : localizedTitle;
    const formSummary =
        activeLocalizedDraft?.initialized ? activeLocalizedDraft.summary
        : isLocalizedContentFetching ? ""
        : localizedSummary;
    const formContent =
        activeLocalizedDraft?.initialized ? activeLocalizedDraft.content
        : isLocalizedContentFetching ? ""
        : localizedContent;
    const formDocumentUrl =
        activeLocalizedDraft?.initialized ? activeLocalizedDraft.documentUrl
        : isLocalizedContentFetching ? ""
        : localizedDocumentUrl;
    const fallbackConsentItem = useMemo(
        () =>
            preselectedConsentItem ?
                mapConsentItemToOption(preselectedConsentItem)
            :   getFallbackConsentItemOption(consentVersion?.consentItem),
        [consentVersion?.consentItem, preselectedConsentItem],
    );
    const itemOptions = useMemo<ConsentItemOption[]>(() => {
        const mappedItems = consentItems
            .filter((item) => (consentItemType ? item.type === consentItemType : true))
            .map(mapConsentItemToOption);

        if (!fallbackConsentItem) {
            return mappedItems;
        }

        return mappedItems.some((item) => item["@id"] === fallbackConsentItem["@id"]) ? mappedItems : (
                [fallbackConsentItem, ...mappedItems]
            );
    }, [consentItemType, consentItems, fallbackConsentItem]);
    const selectedConsentItemIri = watch("consentItemIri");
    const consentVersionGlobalSyncKey = getConsentVersionGlobalSyncKey(consentVersion, preselectedConsentItem);
    const isGlobalContentLoading = consentVersion !== undefined && globalContentSyncKey !== consentVersionGlobalSyncKey;
    const selectedConsentItem = useMemo(
        () =>
            consentItems.find((item) => item["@id"] === selectedConsentItemIri) ??
            (preselectedConsentItem?.["@id"] === selectedConsentItemIri ? preselectedConsentItem : undefined),
        [consentItems, preselectedConsentItem, selectedConsentItemIri],
    );
    const reconsentImpactEstimate = selectedConsentItem?.reconsentImpactEstimate ?? 0;
    const impactedUsersLabel = `${new Intl.NumberFormat(undefined).format(reconsentImpactEstimate)} ${
        reconsentImpactEstimate > 1 ? "users" : "user"
    } will need to consent again`;

    useEffect(() => {
        if (!open) {
            return;
        }

        setEditingLocale(locale);
        setLocalizedDrafts({});
    }, [locale, open]);

    useEffect(() => {
        if (!open || isLocalizedContentFetching) {
            return;
        }

        setLocalizedDrafts((previousDrafts) => {
            const currentDraft = previousDrafts[editingLocale];

            if (currentDraft?.dirty) {
                return previousDrafts;
            }

            if (
                currentDraft?.initialized &&
                currentDraft.title === localizedTitle &&
                currentDraft.summary === localizedSummary &&
                currentDraft.content === localizedContent &&
                currentDraft.documentUrl === localizedDocumentUrl
            ) {
                return previousDrafts;
            }

            return {
                ...previousDrafts,
                [editingLocale]: {
                    content: localizedContent,
                    dirty: false,
                    documentUrl: localizedDocumentUrl,
                    initialized: true,
                    summary: localizedSummary,
                    title: localizedTitle,
                },
            };
        });
    }, [
        editingLocale,
        isLocalizedContentFetching,
        localizedContent,
        localizedDocumentUrl,
        localizedSummary,
        localizedTitle,
        open,
    ]);

    useEffect(() => {
        if (!open || isLocalizedContentLoading || activeLocalizedDraft?.dirty) {
            return;
        }

        reset({
            consentItemIri: preselectedConsentItem?.["@id"] ?? getConsentItemIri(consentVersion?.consentItem),
            version: consentVersion?.version ?? "",
            title: formTitle,
            summary: formSummary,
            content: formContent,
            documentUrl: formDocumentUrl,
            published: consentVersion?.published ?? false,
        });
        setGlobalContentSyncKey(consentVersionGlobalSyncKey);
    }, [
        activeLocalizedDraft?.dirty,
        consentVersion,
        consentVersionGlobalSyncKey,
        formContent,
        formDocumentUrl,
        formSummary,
        formTitle,
        isLocalizedContentLoading,
        open,
        preselectedConsentItem,
        reset,
    ]);

    const updateLocalizedDraft = useCallback(
        (patch: Partial<Pick<ConsentVersionLocalizedDraft, "content" | "documentUrl" | "summary" | "title">>) => {
            setLocalizedDrafts((previousDrafts) => {
                const currentDraft = previousDrafts[editingLocale] ?? {
                    content: getValues("content") ?? "",
                    dirty: false,
                    documentUrl: getValues("documentUrl") ?? "",
                    initialized: true,
                    summary: getValues("summary") ?? "",
                    title: getValues("title") ?? "",
                };

                return {
                    ...previousDrafts,
                    [editingLocale]: {
                        ...currentDraft,
                        ...patch,
                        dirty: true,
                        initialized: true,
                    },
                };
            });
        },
        [editingLocale, getValues],
    );

    const persistCurrentLocalizedDraft = useCallback(() => {
        const values = getValues();
        const nextContent = values.content ?? "";
        const nextDocumentUrl = values.documentUrl ?? "";
        const nextSummary = values.summary ?? "";
        const nextTitle = values.title ?? "";

        setLocalizedDrafts((previousDrafts) => ({
            ...previousDrafts,
            [editingLocale]: {
                content: nextContent,
                dirty:
                    previousDrafts[editingLocale]?.dirty ||
                    previousDrafts[editingLocale]?.content !== nextContent ||
                    previousDrafts[editingLocale]?.documentUrl !== nextDocumentUrl ||
                    previousDrafts[editingLocale]?.summary !== nextSummary ||
                    previousDrafts[editingLocale]?.title !== nextTitle,
                documentUrl: nextDocumentUrl,
                initialized: true,
                summary: nextSummary,
                title: nextTitle,
            },
        }));
    }, [editingLocale, getValues]);

    const handleEditingLocaleChange = (nextLocale: string) => {
        const values = getValues();
        const nextDraft = localizedDrafts[nextLocale];

        persistCurrentLocalizedDraft();
        setEditingLocale(nextLocale);
        reset({
            ...values,
            content: nextDraft?.initialized ? nextDraft.content : "",
            documentUrl: nextDraft?.initialized ? nextDraft.documentUrl : "",
            summary: nextDraft?.initialized ? nextDraft.summary : "",
            title: nextDraft?.initialized ? nextDraft.title : "",
        });
    };

    const handleClose = () => {
        reset();
        setGlobalContentSyncKey(null);
        setLocalizedDrafts({});
        onClose();
    };

    const onSubmitHandler: SubmitHandler<ConsentItemVersionFormData> = async (values) => {
        if (isArchivedVersion) {
            return;
        }

        const draftsToSave = {
            ...localizedDrafts,
            [editingLocale]: {
                content: values.content ?? "",
                dirty: true,
                documentUrl: values.documentUrl ?? "",
                initialized: true,
                summary: values.summary ?? "",
                title: values.title,
            },
        };
        const localizedEntries = Object.entries(draftsToSave).filter(
            ([draftLocale, draft]) => draft.title.trim().length > 0 && (draftLocale === editingLocale || draft.dirty),
        );

        try {
            let savedVersion: ConsentGovernanceVersion | undefined;
            let savedVersionId = consentVersion?.id;

            for (const [draftLocale, draft] of localizedEntries) {
                savedVersion = await mutation.mutateAsync({
                    id: savedVersionId,
                    data: buildConsentVersionPayload(values, draft),
                    locale: draftLocale,
                });
                savedVersionId ??= savedVersion.id;
            }

            if (savedVersion) {
                snackbar.setSnackbarProps({
                    message:
                        consentVersion ?
                            t("settings.consent.version.updated", "Consent version updated successfully")
                        :   t("settings.consent.version.created", "Consent version created successfully"),
                    type: "success",
                });
                onSuccess?.(savedVersion);
                handleClose();
            }
        } catch (error) {
            const fallbackMessage = t("settings.consent.version.error", "Failed to save the consent version.");

            snackbar.setSnackbarProps({
                message: getConsentGovernanceErrorMessage(error, t, fallbackMessage),
                type: "error",
            });
        }
    };

    const selectedConsentItemLabel =
        consentItemType === "document" ? t("settings.consent.version.document", "Document")
        : consentItemType === "opt_in" ? t("settings.consent.version.preference", "Preference")
        : t("settings.consent.version.item", "Consent item");

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: (theme) => theme.spacing(2),
                },
            }}
        >
            <DialogTitle sx={{ px: 4, py: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3" sx={{ fontSize: "22px", fontWeight: 700 }}>
                        {isArchivedVersion ?
                            t("settings.consent.version.view_archived", "Archived version")
                        : consentVersion ?
                            t("settings.consent.version.edit", "Edit version")
                        :   t("settings.consent.version.add", "Add version")}
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <FoodsIcon icon="close" size={1.5} />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 4, pb: 3 }}>
                <Stack component="form" gap={3} onSubmit={handleSubmit(onSubmitHandler)}>
                    <ConsentVersionGlobalFields
                        control={control}
                        errors={errors}
                        impactedUsersLabel={impactedUsersLabel}
                        isArchivedVersion={isArchivedVersion}
                        isGlobalContentLoading={isGlobalContentLoading}
                        itemOptions={itemOptions}
                        selectedConsentItem={selectedConsentItem}
                        selectedConsentItemLabel={selectedConsentItemLabel}
                    />
                    <ConsentVersionLocalizedFields
                        consentVersionId={consentVersion?.id}
                        control={control}
                        editingLocale={editingLocale}
                        errors={errors}
                        hasAnyLocalizedConsentVersionField={hasAnyLocalizedConsentVersionField}
                        isArchivedVersion={isArchivedVersion}
                        isLocalizedContentFetching={isLocalizedContentFetching}
                        isLocalizedContentLoading={isLocalizedContentLoading}
                        localeOptions={localeOptions}
                        mutationIsLoading={mutation.isLoading}
                        open={open}
                        onEditingLocaleChange={handleEditingLocaleChange}
                        onLocalizedDraftChange={updateLocalizedDraft}
                    />
                    <DialogActions sx={{ px: 0, pb: 0 }}>
                        <Button variant="secondary" onClick={handleClose}>
                            {isArchivedVersion ? t("close", "Close") : t("cancel", "Cancel")}
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={
                                !isValid ||
                                mutation.isLoading ||
                                isGlobalContentLoading ||
                                isLocalizedContentLoading ||
                                itemOptions.length === 0 ||
                                isArchivedVersion
                            }
                        >
                            {mutation.isLoading ? t("common.saving", "Saving...") : t("common.save", "Save")}
                        </Button>
                    </DialogActions>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
